/**
 * The comma-separated options list behind `dropdown` and `single_choice` fields.
 *
 * 🚨 **A comma cannot be typed into a plainly-controlled input here**, which is what this module
 * exists to prevent. The obvious binding —
 *
 * ```tsx
 * value={field.options.join(', ')}
 * onChange={(v) => onPatch({ options: v.split(',').map((s) => s.trim()).filter(Boolean) })}
 * ```
 *
 * — destroys the character on the keystroke that types it: `"Rojo,"` parses to `['Rojo']` (the
 * `filter` drops the empty tail), which re-renders as `"Rojo"`. The merchant presses `,`, nothing
 * appears, and every option runs together — reported as `test1asdasasdasd`.
 *
 * The fix is to stop deriving the input's text from the parsed array while the merchant is typing.
 * The draft is what they see; the array is what the config stores. They re-converge on blur, which
 * is also when spacing is normalised and a trailing comma disappears.
 */

/**
 * Parse the merchant's text into the stored options.
 *
 * Empty segments are dropped so a trailing comma or a double comma never becomes a blank choice in
 * the rendered form. This is deliberately lossy — which is exactly why it must not drive the input.
 */
export function parseOptions(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * What the input shows: the merchant's own text while they are editing it, and the canonical form
 * otherwise.
 *
 * `null` means "not editing" — not "empty". An empty draft is a real state (the merchant cleared
 * the field) and must survive, so the check is on `null`, never on falsiness.
 */
export function optionsText(draft: string | null, options: readonly string[]): string {
  return draft ?? options.join(', ');
}
